/**
 * O **nome exibido** da Empresa (13.7, FR-25 emendado): o nome fantasia — como a equipe chama a
 * empresa, o nome que vai na personalização —, e a razão social na falta dele.
 *
 * É o único lugar do SPA que decide qual dos dois aparece: card do Funil, Hoje, Tarefas, Aguardando
 * entrega, Originados, Relatórios, WhatsApp, a ficha do Negócio, a lista de Empresas e de Pessoas, a
 * sugestão do campo de Empresa. A razão social continua na ficha da Empresa e na do Negócio.
 *
 * **"O nome fantasia conta"** quando, aparado, ele tem ao menos uma letra ou um dígito: "." ou "—"
 * (o que o ERP às vezes guarda) e o espaço invisível não são nome de empresa. A regra é a mesma do
 * `cadastro`, do `integracao-erp` e do `comunicacao` (`nome_fantasia_que_conta`).
 */

const TEM_LETRA_OU_DIGITO = /[\p{L}\p{N}]/u

/**
 * O espaço e os caracteres de formato (`Cf`: o espaço de largura zero, o BOM) nas pontas. O `trim()`
 * sozinho não tira o `U+200B`, e o `strip()` do Python não tira o `U+FEFF` — as quatro cópias da
 * regra aparam o mesmo conjunto.
 */
const NAS_PONTAS = /^[\s\p{Cf}]+|[\s\p{Cf}]+$/gu

/** O nome fantasia aparado, ou `null` quando ele não tem letra nem dígito. */
export function nomeFantasiaQueConta(texto: string | null | undefined): string | null {
  if (texto === null || texto === undefined) {
    return null
  }
  const aparado = texto.replace(NAS_PONTAS, '')
  return TEM_LETRA_OU_DIGITO.test(aparado) ? aparado : null
}

/** O nome exibido: o nome fantasia que conta, senão a razão social. */
export function nomeExibido(razaoSocial: string, nomeFantasia: string | null | undefined): string {
  return nomeFantasiaQueConta(nomeFantasia) ?? razaoSocial
}

/**
 * O nome exibido de uma Empresa lida do `cadastro`. A chave `nome_fantasia` pode faltar — o backend
 * anterior à 13.7 não a manda —, e isso vale "sem nome fantasia".
 */
export function nomeExibidoDaEmpresa(empresa: {
  readonly razao_social: string
  readonly nome_fantasia?: string | null
}): string {
  return nomeExibido(empresa.razao_social, empresa.nome_fantasia)
}

/**
 * As letras que o NFKD não decompõe, e o `ß` que o `casefold` do Python expande — o mapa de
 * `_SEM_DECOMPOSICAO` de `servicos/cadastro/dominio/busca.py`, para os dois lados concordarem.
 */
const SEM_DECOMPOSICAO: Readonly<Record<string, string>> = {
  ß: 'ss',
  æ: 'ae',
  ø: 'o',
  œ: 'oe',
  ł: 'l',
  đ: 'd',
  þ: 'th',
}

/**
 * A forma de comparar dois nomes "a olho": sem acento, sem pontuação, sem caixa, `&` valendo `e` — a
 * regra da coluna sombra da busca (`normalizar_para_busca`, no `cadastro`), na mesma ordem.
 */
export function normalizarParaComparar(texto: string): string {
  return texto
    .replace(/&/g, ' e ')
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[ßæøœłđþ]/g, (letra) => SEM_DECOMPOSICAO[letra] ?? letra)
    .replace(/[^0-9a-z]/g, '')
}

/**
 * A razão social para mostrar **abaixo** do nome exibido — só quando o nome fantasia conta e é outra
 * coisa que a razão social. Sem isso, a mesma linha apareceria duas vezes.
 */
export function razaoSocialQueDifere(
  razaoSocial: string,
  nomeFantasia: string | null | undefined,
): string | null {
  const nome = nomeFantasiaQueConta(nomeFantasia)
  if (nome === null) {
    return null
  }
  return normalizarParaComparar(nome) === normalizarParaComparar(razaoSocial) ? null : razaoSocial
}
